import React from "react";
import { Tooltip } from "recharts";
type CustomTooltipProps = React.ComponentProps<typeof Tooltip> & {
  formatValue: (value: number) => string;
  valueLabel?: string;
  nameLabel: string;
  /** Metric acronym shown beside each value; omitted drops the column entirely. */
  metricLabel?: string;
  sort?: "asc" | "desc" | "value-asc" | "value-desc";
  selectedDataKeys?: string[];
  columnKeySuffix?: string;
  columnLabels?: [React.ReactNode, React.ReactNode];
  tag?: React.ReactNode;
};
export const CustomTooltip = ({
  active,
  payload,
  nameLabel,
  metricLabel,
  formatValue,
  valueLabel,
  sort = "asc",
  selectedDataKeys,
  columnKeySuffix,
  columnLabels,
  tag
}: CustomTooltipProps) => {
  if (!active || !payload || payload.length === 0) return null;
  const entries = payload.filter(entry => entry.strokeWidth && entry.type !== "none").sort((a, b) => {
    if (sort === "value-asc") {
      return ((a.value ?? 0) as number) - ((b.value ?? 0) as number);
    }
    if (sort === "value-desc") {
      return ((b.value ?? 0) as number) - ((a.value ?? 0) as number);
    }
    if (sort === "desc") {
      return -String(a.name ?? "").localeCompare(String(b.name ?? ""));
    }
    return String(a.name ?? "").localeCompare(String(b.name ?? ""));
  });
  const year = valueLabel ?? entries[0]?.payload?.year;
  const metricCell = metricLabel ? <span className="text-sm text-gray-400">{metricLabel}</span> : null;
  const valueColumns = metricLabel ? "auto auto" : "auto";

  // if in comparison mode, show 2 columns of tooltip by suffix
  if (columnKeySuffix && columnLabels) {
    const defaultEntries = entries.filter(e => !String(e.dataKey ?? "").endsWith(columnKeySuffix));
    const suffixedEntries = entries.filter(e => String(e.dataKey ?? "").endsWith(columnKeySuffix));
    const renderColumn = (list: typeof entries) => list.map((entry, i) => {
      const isSelected = selectedDataKeys?.length && selectedDataKeys.includes(entry.dataKey as string);
      return <React.Fragment key={`${entry.dataKey}_${i}`}>
            <span className="flex items-center gap-1 text-sm">
              <div className={`h-2.5 w-2.5 shrink-0 rounded-sm`} style={{
            backgroundColor: entry.color
          }} />
              <span className={isSelected ? "font-semibold" : ""}>{entry.name}</span>
            </span>
            <span className={`text-right text-sm tabular-nums ${isSelected ? "font-semibold" : "font-medium"}`}>
              {formatValue(Number(entry.value ?? 0))}
            </span>
            {metricCell}
          </React.Fragment>;
    });
    return <div className="rounded-lg border border-gray-200 bg-surface/80 p-3 text-gray-700 shadow-lg backdrop-blur backdrop-saturate-200">
        <div className="mb-2 flex items-center gap-2">
          <span className="font-semibold">{nameLabel}</span>
          <span className="font-semibold tabular-nums">{String(year)}</span>
        </div>
        <div className="grid grid-cols-2 gap-x-4">
          <p className="mb-1 flex items-center gap-1 text-xs font-semibold text-gray-500">{columnLabels[0]}</p>
          <p className="mb-1 flex items-center gap-1 text-xs font-semibold text-gray-500">{columnLabels[1]}</p>
          <div className="grid gap-x-2 gap-y-1" style={{
          gridTemplateColumns: `1fr ${valueColumns}`
        }}>
            {renderColumn(defaultEntries)}
          </div>
          <div className="grid gap-x-2 gap-y-1" style={{
          gridTemplateColumns: `1fr ${valueColumns}`
        }}>
            {renderColumn(suffixedEntries)}
          </div>
        </div>
      </div>;
  }
  return <div className="rounded-lg border border-gray-200 bg-surface/80 p-3 text-gray-700 shadow-lg backdrop-blur backdrop-saturate-200" data-sentry-component="CustomTooltip" data-sentry-source-file="CustomTooltip.tsx">
      {tag && <div className="mb-2">{tag}</div>}
      <div className="grid gap-x-2" style={{
      gridTemplateColumns: `minmax(72px, auto) ${valueColumns}`
    }}>
        <span className="mb-2 font-semibold">{nameLabel}</span>
        <span className="font-semibold tabular-nums">{year}</span>
        {metricLabel && <span className="mb-2" />}
        {entries.map((entry, i) => {
        const isSelected = selectedDataKeys?.length && selectedDataKeys.includes(entry.dataKey as string);
        return <React.Fragment key={`${entry.dataKey}_${i}`}>
              <span className={`flex items-center gap-1 text-sm ${isSelected ? "font-semibold" : ""}`}>
                <div className="h-3 w-3 rounded-sm" style={{
              backgroundColor: entry.color
            }} />
                {entry.name}
              </span>
              <span className={`text-right tabular-nums ${isSelected ? "font-semibold" : ""}`}>
                {formatValue(Number(entry.value ?? 0))}
              </span>
              {metricCell}
            </React.Fragment>;
      })}
      </div>
    </div>;
};